import { Card, Avatar, Typography, Box } from "@mui/material";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { Person } from "../types/person";
import React from "react";

interface Props {
  partner1: Person;
  partner2?: Person;
  isEdge?: boolean;
  onPersonClick?: (person: Person) => void;
  registerPosition?: (id: string, rect: DOMRect) => void;
  formatPersonSubtitle?: (person: Person) => string;
  coupleNodeWidth?: number;
}

export default function CoupleCard({
  partner1,
  partner2,
  isEdge,
  onPersonClick,
  registerPosition,
  formatPersonSubtitle,
  coupleNodeWidth = 320,
}: Props) {
  const ref = React.useRef<HTMLDivElement>(null);

  React.useLayoutEffect(() => {
    if (ref.current && registerPosition) {
      // register using first partner id
      registerPosition(partner1.id, ref.current.getBoundingClientRect());
      if (partner2)
        registerPosition(partner2.id, ref.current.getBoundingClientRect());
    }
  });

  const renderPersonRow = (person: Person) => {
    let subtitle = "";
    if (formatPersonSubtitle) {
      subtitle = formatPersonSubtitle(person);
    } else {
      // Default format: dates + ID
      let lifeText = "";
      if (person.birth && person.death) {
        lifeText = `${person.birth} – ${person.death}`;
      } else if (person.birth) {
        lifeText = `${person.birth}`;
      } else if (person.death) {
        lifeText = `– ${person.death}`;
      }
      subtitle = lifeText ? `${lifeText} • ${person.id}` : person.id;
    }
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          py: 1,
          cursor: "pointer",
          "&:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.04)",
          },
        }}
        onClick={() => onPersonClick?.(person)}
      >
        <Avatar
          src={person.imageUrl}
          alt={person.name}
          sx={{ width: 56, height: 56, mr: 2 }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="h6"
            sx={{
              lineHeight: 1,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {person.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <Card
      sx={{
        my: 1,
        width: coupleNodeWidth,
        borderRadius: 2,
        boxShadow: 3,
        position: "relative",
      }}
      ref={ref}
    >
      <Box sx={{ px: 2, py: 1 }}>
        {renderPersonRow(partner1)}
        {partner2 && renderPersonRow(partner2)}
        {isEdge && (
          <ArrowForwardIosIcon
            sx={{ position: "absolute", right: 8, top: 8, fontSize: 18 }}
          />
        )}
      </Box>
    </Card>
  );
}
